import { assertNever } from '@eversports/utility-types'
import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { motion } from '@eversports/react-components/design-tokens/motion'
import useCart from '../../data/useCart'

import useCheckoutContext from '../../data/useCheckoutContext'
import useMutationWithErrorHandling from '../../data/useMutationWithErrorHandling'
import getPhoenixUrlParams from '../../lib/get-phoenix-url-params'
import { Localized } from '../../localization/react'

import {
  VoucherSelectionCartDocument,
  VoucherSelectionCartQuery,
  VoucherSelectionSetVoucherOnCartDocument,
  VoucherSelectionSetVoucherOnCartMutation,
  VoucherSelectionSetVoucherOnCartMutationVariables,
} from '../../graphql'
import { isPriceTableOfType } from '../../typings/PriceTable'
import VoucherSelectionActive from './VoucherSelectionActive'
import VoucherSelectionReady from './VoucherSelectionReady'
import VoucherSelectionWrapper, { Helper } from './VoucherSelectionWrapper'

interface Props {
  initialState?: VoucherSelectionState
}

export enum VoucherSelectionState {
  READY,
  ACTIVE,
  DONE,
}

const VoucherSelection: React.FC<React.PropsWithChildren<Props>> = ({ initialState }) => {
  const location = useLocation()
  const [state, setStateDirectly] = React.useState<VoucherSelectionState>(initialState || VoucherSelectionState.READY)
  const [helper, setHelper] = React.useState<Helper | undefined>()
  const { cartId } = getPhoenixUrlParams(location.search)
  const [setVoucherOnCart, { data: mutationData, loading: mutationLoading }] = useMutationWithErrorHandling<
    VoucherSelectionSetVoucherOnCartMutation,
    VoucherSelectionSetVoucherOnCartMutationVariables
  >(VoucherSelectionSetVoucherOnCartDocument, {})
  const { cartData: queryData, isCartLoading: queryLoading } = useCart<VoucherSelectionCartQuery>(
    VoucherSelectionCartDocument,
    {
      skip: !cartId,
    },
  )
  const context = useCheckoutContext()

  // Set Voucher State utility wrapper
  const setState = (newState: VoucherSelectionState, newHelper?: Helper) => {
    setStateDirectly(newState)
    setHelper(newHelper)
  }

  useEffect(() => {
    const hasVoucherApplied = queryData?.cart.priceTable?.itemsV3.some(isPriceTableOfType('PriceTableItemVoucher'))

    if (!hasVoucherApplied && state === VoucherSelectionState.DONE) {
      setState(VoucherSelectionState.READY)
    }
  }, [queryData])

  // Add handler for the voucher being removed to the context event bus
  useEffect(() => {
    const handleVoucherRemoval = () => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      setVoucher()
    }
    context.registerVoucherRemovalHandler(handleVoucherRemoval)
  }, [])

  // Derive initial state of voucher selection from cart summary
  useEffect(() => {
    if (!queryData) return

    const hasVoucher = queryData.cart.priceTable?.itemsV3.some(isPriceTableOfType('PriceTableItemVoucher'))

    if (hasVoucher) {
      setState(VoucherSelectionState.DONE)
    }
  }, [queryLoading])

  // Set/Unset voucher on remote service
  const setVoucher = async (voucher?: string) => {
    if (!cartId) return

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    setVoucherOnCart({
      variables: {
        cartId,
        voucher,
      },
    })
  }

  const handleAddVoucherClick = () => {
    setState(VoucherSelectionState.ACTIVE)
  }

  // Set component state based on mutation response
  useEffect(() => {
    const data = mutationData && mutationData.setVoucherOnCart
    if (!data || data.__typename === 'ExpectedErrors') return

    // Upon successful response Decide whether to show or hide the component based on a voucher being set or not
    if (data.priceTable && data.priceTable.itemsV3.some(isPriceTableOfType('PriceTableItemVoucher'))) {
      return setState(VoucherSelectionState.DONE, {
        isError: false,
        text: <Localized id="voucher-successfully-applied" />,
      })
    }
    return setState(VoucherSelectionState.READY)
  }, [mutationData])

  // Do not render anything if
  // a) vouchers are not enabled for this checkout
  // b) there is a corporate budget applied
  if (
    !cartId ||
    (queryData &&
      (!queryData.cart.isVoucherEnabled ||
        queryData.cart.priceTable?.itemsV3.some(isPriceTableOfType('PriceTableItemBudget'))))
  ) {
    return null
  }

  // Render components based on application state
  switch (state) {
    case VoucherSelectionState.READY:
      return (
        <VoucherSelectionWrapper helper={helper}>
          <VoucherSelectionReady onAddVoucherClick={handleAddVoucherClick} />
        </VoucherSelectionWrapper>
      )
    case VoucherSelectionState.ACTIVE:
      return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <VoucherSelectionWrapper helper={helper}>
            <VoucherSelectionActive onApplyVoucher={setVoucher} loading={queryLoading || mutationLoading} />
          </VoucherSelectionWrapper>
        </motion.div>
      )
    case VoucherSelectionState.DONE:
      return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <VoucherSelectionWrapper helper={helper} />
        </motion.div>
      )
    default:
      return assertNever(state)
  }
}

export default VoucherSelection
